import React from 'react';
import * as Sentry from '@sentry/react';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
}

function RouteErrorFallback({
  resetError,
}: {
  resetError: () => void;
}): React.ReactElement {
  return (
    <div className="p-6 border border-red-200 rounded-lg bg-white shadow-md">
      <div className="text-center mb-4">
        <svg
          className="w-12 h-12 text-red-500 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">This section failed to load</h3>
      <p className="text-gray-600 mb-4 text-center text-sm">
        Something went wrong here. You can try again or continue using the rest of the app.
      </p>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={resetError}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

/**
 * Per-route error boundary for data-heavy pages. Shows error in layout instead of full-page takeover.
 */
export function RouteErrorBoundary({ children }: RouteErrorBoundaryProps): React.ReactElement {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <RouteErrorFallback resetError={resetError} />}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
